import { useCallback, useEffect, useState } from 'react';
import { formatCurrencyWhole as formatCurrency } from '../lib/format';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ChevronLeft, ChevronRight, RefreshCw, UserCheck, AlertCircle
} from 'lucide-react';
import apiClient from '../lib/api';

type DateRange = 'all' | '30d' | '90d' | '365d';

interface PhysicianRankingRow {
  physicianId: string;
  physicianName: string;
  clinicName: string | null;
  orderCount: number;
  revenue: number;
  averageOrderValue: number;
  averageOrderInterval: number | null;
  firstOrderDate: string | null;
  lastOrderDate: string | null;
}

const PAGE_SIZE = 25;

function formatDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminPhysicianRankings() {
  const navigate = useNavigate();
  const [range, setRange] = useState<DateRange>('all');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<PhysicianRankingRow[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, totalCount: 0, totalPages: 1, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const computeDates = useCallback((): { startDate?: Date; endDate?: Date } => {
    if (range === 'all') return {};
    const days = range === '30d' ? 30 : range === '90d' ? 90 : 365;
    return { startDate: new Date(Date.now() - days * 24 * 60 * 60 * 1000), endDate: new Date() };
  }, [range]);

  const fetchRankings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.getAdminPhysicianRankings({ ...computeDates(), page, limit: PAGE_SIZE });
      if (res.success && res.data) {
        setRows(res.data.rankings);
        setPagination(res.data.pagination);
      } else {
        setError(res.error || 'Failed to load rankings');
      }
    } catch {
      setError('Failed to load rankings');
    } finally {
      setLoading(false);
    }
  }, [computeDates, page]);

  useEffect(() => { fetchRankings(); }, [fetchRankings]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <button
            onClick={() => navigate('/analytics')}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Analytics
          </button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserCheck className="w-6 h-6" /> Physician Rankings
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Full leaderboard sorted by revenue. {pagination.totalCount > 0 && `${pagination.totalCount} physicians total.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['all', '30d', '90d', '365d'] as DateRange[]).map(r => (
            <button
              key={r}
              onClick={() => { setRange(r); setPage(1); }}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                range === r ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {r === 'all' ? 'All time' : r}
            </button>
          ))}
          <button onClick={fetchRankings} className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 overflow-hidden">
        {error ? (
          <div className="flex items-center justify-center p-8 text-red-600">
            <AlertCircle className="w-5 h-5 mr-2" /> {error}
          </div>
        ) : loading && rows.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
            <span className="ml-2 text-gray-600">Loading rankings...</span>
          </div>
        ) : (
          <>
            <div className="data-table-container">
              <div className="data-table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Physician</th>
                      <th>Clinic</th>
                      <th>Orders</th>
                      <th>Total Revenue</th>
                      <th>Avg Order Value</th>
                      <th>Avg Interval</th>
                      <th>Last Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-8 text-gray-500">
                          No physicians match this filter.
                        </td>
                      </tr>
                    ) : rows.map((r, i) => {
                      const rank = (page - 1) * PAGE_SIZE + i + 1;
                      return (
                        <tr key={r.physicianId}>
                          <td className="cell-secondary">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold
                              ${rank === 1 ? 'bg-yellow-500' : rank === 2 ? 'bg-gray-400' : rank === 3 ? 'bg-amber-600' : 'bg-gray-300'}`}>
                              #{rank}
                            </div>
                          </td>
                          <td className="cell-primary">{r.physicianName}</td>
                          <td className="cell-muted">{r.clinicName || '—'}</td>
                          <td className="cell-secondary">{r.orderCount.toLocaleString()}</td>
                          <td className="cell-mono font-semibold">{formatCurrency(r.revenue)}</td>
                          <td className="cell-mono">{formatCurrency(r.averageOrderValue)}</td>
                          <td className="cell-secondary">
                            {r.averageOrderInterval !== null ? `${r.averageOrderInterval} days` : '—'}
                          </td>
                          <td className="cell-muted">{formatDate(r.lastOrderDate)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, pagination.totalCount)} of {pagination.totalCount}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-700">Page {page} of {pagination.totalPages}</span>
                  <button
                    onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                    disabled={page === pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
